import { fetchV3, fetchV3Payload, isEmptySuccess } from "./client";

const emr = (path) => `electronic-medical-record/${path}`;

export const listEmrServiceRequests = (body, { useLoading = false } = {}) =>
  fetchV3({
    url: emr("service-requests/"),
    body,
    useLoading,
  });

export const listFollowUps = (uuid) =>
  fetchV3({ url: emr(`follow-up/${uuid}/`), useLoading: false });

export const saveFollowUp = (request, data, uuid) =>
  fetchV3({
    url: emr(`follow-up/${request}/${uuid ? `${uuid}/` : ""}`),
    method: uuid ? "PUT" : "POST",
    body: data,
    useLoading: true,
  });

export const deleteFollowUpRequest = async (request, uuid) => {
  const resp = await fetchV3({
    url: emr(`follow-up/${request}/${uuid}/`),
    method: "DELETE",
    useLoading: true,
  });
  return isEmptySuccess(resp);
};

export const listServiceRequestAttachments = (uuid) =>
  fetchV3({
    url: emr(`service-request-attachments/${uuid}/`),
    useLoading: false,
  });

export const saveServiceRequestAttachment = (
  request,
  data,
  uuid,
  notStringify,
) =>
  fetchV3(
    {
      url: emr(
        `service-request-attachments/${request}/${uuid ? `${uuid}/` : ""}`,
      ),
      method: uuid ? "PATCH" : "POST",
      body: data,
      useLoading: true,
    },
    notStringify,
  );

export const deleteServiceRequestAttachment = async (request, uuid) => {
  const resp = await fetchV3({
    url: emr(`service-request-attachments/${request}/${uuid}/`),
    method: "DELETE",
    useLoading: true,
  });
  return isEmptySuccess(resp);
};

export const listAdditionalInfo = (request) =>
  fetchV3({
    url: emr(`additional-info/${request}/`),
    method: "GET",
    useLoading: false,
  });

export const saveAdditionalInfo = (request, newText, oldData) =>
  fetchV3({
    url: oldData
      ? emr(`additional-info-instance/${oldData.uuid}/`)
      : emr(`additional-info/${request}/`),
    method: oldData ? "PATCH" : "POST",
    body: { text: newText },
    useLoading: true,
  });

export const deleteAdditionalInfoRequest = async (uuid) => {
  const resp = await fetchV3({
    url: emr(`additional-info-instance/${uuid}/`),
    method: "DELETE",
    useLoading: true,
  });
  return isEmptySuccess(resp);
};

export const getOrUpdatePastMedicalHistory = (
  uuid,
  data = null,
  extraParam,
  { silent = false } = {},
) => {
  const query = extraParam ? `?visit_note_uuid=${extraParam}` : "";
  return fetchV3({
    url: emr(`past-medical-history/${uuid}/${query}`),
    method: data ? "PATCH" : "GET",
    body: data,
    useLoading: data ? !silent : false,
    silent,
  });
};

export const listPmhAttachments = (uuid) =>
  fetchV3({
    url: emr(`past-medical-history-attachments/${uuid}/`),
    useLoading: false,
  });

export const savePmhAttachment = (patientUuid, data, uuid, notStringify) =>
  fetchV3(
    {
      url: emr(
        `past-medical-history-attachments/${patientUuid}/${uuid ? `${uuid}/` : ""}`,
      ),
      method: uuid ? "PATCH" : "POST",
      body: data,
      useLoading: true,
    },
    notStringify,
  );

export const deletePmhAttachment = async (patientUuid, attachmentUuid) => {
  const resp = await fetchV3({
    url: emr(
      `past-medical-history-attachments/${patientUuid}/${attachmentUuid}/`,
    ),
    method: "DELETE",
    useLoading: true,
  });
  return isEmptySuccess(resp);
};

export const listCommonTemplates = () =>
  fetchV3({
    url: emr("common-emr-templates/"),
    useLoading: false,
  });

export const getCommonTemplate = (templateUuid) =>
  fetchV3({
    url: emr(`common-emr-templates/${templateUuid}/`),
    useLoading: false,
  });

export const getEmrTemplate = (uuid) =>
  fetchV3({
    url: emr(`emr-templates/${uuid}/`),
    useLoading: false,
  });

export const saveEmrTemplate = (body, uuid, create, { silent = false } = {}) =>
  fetchV3({
    url: emr(`emr-templates/${create ? `${uuid}/` : ""}`),
    method: create ? "PATCH" : "POST",
    body,
    useLoading: !silent,
    silent,
  });

export const deleteEmrTemplate = (uuidRequest) =>
  fetchV3({
    url: emr(`emr-templates/${uuidRequest}/`),
    method: "DELETE",
    useLoading: true,
  });

export const getExcuseDoc = (uuidRequest) =>
  fetchV3({
    url: emr(`excuse/${uuidRequest}/`),
    useLoading: false,
  });

export const saveExcuseDoc = (uuidRequest, create, body) =>
  fetchV3({
    url: emr(`excuse/${uuidRequest}/`),
    method: create ? "POST" : "PATCH",
    body,
    useLoading: true,
  });

export const getCovidTestResult = (uuidRequest) =>
  fetchV3({
    url: emr(`covid-test-result/${uuidRequest}/`),
    useLoading: false,
  });

export const listFamilySocialHistoryChoices = () =>
  fetchV3({
    url: emr("past-medical-history/family-social-history-choices/"),
    useLoading: false,
  });

export const listCptCodes = () =>
  fetchV3({
    url: emr("cpt-codes/"),
    useLoading: false,
  });

export const saveTemplateData = (uuidEmrNote, body) =>
  fetchV3({
    method: "POST",
    body,
    url: emr(`save-template-data/${uuidEmrNote}/`),
    useLoading: true,
  }).then(fetchV3Payload);

export const getTemplateData = (uuidRequest) =>
  fetchV3({
    url: emr(`template-data/${uuidRequest}/`),
    useLoading: false,
  });

export const getDischargeData = (uuid) =>
  fetchV3({
    url: emr(`discharge-data/${uuid}/`),
    useLoading: false,
  });

export const sendAttachmentsOrFollowUps = (body, type, bool = false) =>
  fetchV3(
    {
      url: emr(
        `send-${type === "attachment" ? "attachments" : "follow-ups"}/`,
      ),
      method: "POST",
      body,
      useLoading: true,
    },
    bool,
  ).then(fetchV3Payload);

export const sendEmrDocs = (body, uuidRequest) =>
  fetchV3(
    {
      url: emr(`send-emr-docs/${uuidRequest}/`),
      method: "POST",
      body,
      useLoading: true,
    },
    true,
  );

export const uploadEmrDocsRequest = (
  body,
  uuidRequest,
  { useLoading = true } = {},
) =>
  fetchV3(
    {
      url: emr(`upload-emr-docs/${uuidRequest}/`),
      method: "POST",
      body,
      useLoading,
    },
    true,
  ).then(fetchV3Payload);

export const uploadFollowUpsRequest = (body, uuidRequest) =>
  fetchV3(
    {
      url: emr(`upload-follow-ups/${uuidRequest}/`),
      method: "POST",
      body,
      useLoading: true,
    },
    true,
  ).then(fetchV3Payload);

export const listIcd10 = () =>
  fetchV3({
    url: emr("icd-10-list/"),
    useLoading: false,
  });

export const getOrUpdateReceipt = (uuid, body) =>
  fetchV3({
    url: emr(`${body ? "update-receipt-data" : "receipt-data"}/${uuid}/`),
    method: body ? "PUT" : "GET",
    body,
    useLoading: !!body,
  });

export const getCurrentMedications = (requestUuid) =>
  fetchV3({
    url: emr(`current-medications/${requestUuid}/`),
    useLoading: false,
    silent: true,
  }).then(fetchV3Payload);
